import cn from 'classnames'
import AlertBanner from 'ext-ui/components/AlertBanner/AlertBanner'
import { warning_text } from 'ext-ui/style/colors'
import AlertCircleIcon from 'ext-ui/svg/icon/AlertCircleIcon'
import React, { ReactElement } from 'react'
import DynamicPricingTierCard, {
  PlanPeriod,
  PricingTierCardCtaAction,
} from 'src/app/Onboarding/DynamicPricingTierCard'
import { CurrencyFormatter } from 'src/localization/currencyFormatter'
import { TranslateFn } from 'src/localization/translate'
import {
  PricingPlan,
  PricingPromotion,
} from 'src/queries/organizations/pricingPlans/pricingPlans.models'

import style from './DynamicSaasOnboardingPricingTiers.style'
import { PricingPresentationAdapter } from './pricingPresentation'

export type DynamicSaasOnboardingPricingTiersProps = {
  pricingPlans: PricingPlan[]
  promotion?: PricingPromotion
  showVirtualCardAlert?: boolean
  isNonExtendIssuer?: boolean
  className?: string
  currencyFormatter: CurrencyFormatter
  translate: TranslateFn
  getPlanCtaAction: (args: {
    plan: PricingPlan
    planPeriod: PlanPeriod
  }) => PricingTierCardCtaAction
  onPlanPeriodChange?: (plan: PricingPlan, planPeriod: PlanPeriod) => void
  presentation?: PricingPresentationAdapter
}

export default function DynamicSaasOnboardingPricingTiers(
  props: DynamicSaasOnboardingPricingTiersProps,
): ReactElement {
  const {
    pricingPlans,
    promotion,
    showVirtualCardAlert = false,
    isNonExtendIssuer = false,
    className = '',
    currencyFormatter,
    translate,
    getPlanCtaAction,
    onPlanPeriodChange,
    presentation,
  } = props

  return (
    <div
      className={cn(
        'DynamicSaasOnboardingPricingTiers',
        className,
        'flex-column gap-10 align-center',
      )}
      css={style}
    >
      {showVirtualCardAlert && (
        <AlertBanner
          className="BrandPartnership-alert"
          variant="warning"
          data-testid="virtual-card-alert"
          icon={<AlertCircleIcon fill={warning_text} />}
          title={translate('Virtual cards unavailable with current bank')}
        >
          <p>
            {translate(
              'Your bank does not have integration with Extend. Partner banks enable secure, real-time virtual card creation.',
            )}
          </p>
          <a
            data-testid={'contact-us-link'}
            aria-label={translate('Contact us to learn more')}
            href={'https://www.paywithextend.com/support-request'}
            className={cn('BrandPartnership-alert-link')}
            target={'_blank'}
            rel={'noopener noreferrer'}
          >
            <p>{translate('Contact us to learn more')}</p>
          </a>
        </AlertBanner>
      )}
      <div className="PricingCards-container">
        {pricingPlans.map((plan, index) => (
          <DynamicPricingTierCard
            key={plan.id}
            plan={plan}
            promotion={promotion}
            planIndex={index}
            previousPlanName={
              index > 0 ? pricingPlans[index - 1].displayName : undefined
            }
            isNonExtendIssuer={isNonExtendIssuer}
            currencyFormatter={currencyFormatter}
            translate={translate}
            getPlanCtaAction={getPlanCtaAction}
            onPlanPeriodChange={onPlanPeriodChange}
            presentation={presentation}
          />
        ))}
      </div>
    </div>
  )
}
